// The page's config, apart from app.ts so the world map (apps/world) can use
// the same lines, systems and view.

import type { GtfsPageConfig, GtfsPageLine } from '@tol/gtfs/page';
import { NYC_LINES } from '../shared/nyc.ts';

// The commuter railroads (LIRR, Metro-North, NJ Transit) are drawn hollow,
// like London's Overground; the subway, PATH and light rail solid.
// The buses are the minor tier (@tol/core/minor): drawn only zoomed in,
// thin and muted, under every other line (they come first), their vehicles
// as dots and then route tags.
const LINES: readonly GtfsPageLine[] = [
  ...NYC_LINES.filter((l) => l.minor === true),
  ...NYC_LINES.filter((l) => l.minor !== true),
].map((l) => {
  const line: GtfsPageLine = {
    id: l.id,
    name: l.name,
    color: l.color,
    show: l.show,
    mode: l.hollow ? 'overground' : 'tube',
    label: l.label,
    dual: l.hollow,
    system: l.system,
    minor: l.minor === true,
  };
  return l.bullet === undefined ? line : Object.assign(line, { bullet: l.bullet });
});

export const NYC_PAGE: GtfsPageConfig = {
  hooks: 'nycLive',
  city: 'nyc',
  lines: LINES,
  systems: [
    { id: 'subway', name: 'Subway' },
    { id: 'path', name: 'PATH' },
    { id: 'lirr', name: 'Long Island Rail Road' },
    { id: 'mnr', name: 'Metro-North' },
    { id: 'njt', name: 'NJ Transit' },
    { id: 'njlr', name: 'NJ Transit Light Rail' },
    // Folded into one row (all minor): shows or hides every bus.
    { id: 'bus', name: 'MTA Buses' },
  ],
  // Midtown and Lower Manhattan; the LIRR to Montauk, Metro-North to
  // Poughkeepsie and New Haven, NJ Transit to Port Jervis, Hackettstown,
  // Trenton, Bay Head and, with the River Line and the Atlantic City line,
  // Camden, Philadelphia and Atlantic City.
  view: {
    bounds: {
      phone: [
        [-74.0, 40.735],
        [-73.965, 40.765],
      ],
      desktop: [
        [-74.02, 40.7],
        [-73.93, 40.8],
      ],
    },
    minZoom: 8,
    maxZoom: 17,
    maxBounds: [
      [-75.6, 39.2],
      [-71.4, 42.1],
    ],
  },
  credits: [
    'Train and bus data and track: <a href="https://www.mta.info/developers">MTA</a>, <a href="https://www.panynj.gov/path/en/index.html">PATH</a>, <a href="https://www.njtransit.com/developer-tools">NJ Transit</a>',
  ],
  // American railways run on the right.
  keepRight: true,
  timeZone: 'America/New_York',
  locale: 'en-US',
  dataName: 'The newest data',
  alerts: true,
};
